import type { PublicationModel } from "../../types/models";
import { COMMITTED_PUBLICATION_STATUSES } from "../../utils/PublicationHelper";
import { isImmutablePub } from "./engine/status";
import { getPublishedCountLabel } from "./publicationStatusPresentation";

export type ShortSelectionMode = "schedule" | "edit";

export type ShortSelectionSummary = {
  /** Visible caption under the short card title; null when nothing to say. */
  label: string | null;
  /** Richer hover / accessible name; null when nothing to say. */
  tooltip: string | null;
};

/** Only posts in the current queue can lock a picker tile. History from
 * other shorts must not turn an unselected tile into a checked one. */
export function getLockedShortIds({
  selectedVideoIds,
  perVideoState,
  mode,
}: {
  selectedVideoIds: readonly string[];
  perVideoState: Readonly<Record<string, { localPublications: PublicationModel[] }>>;
  mode: ShortSelectionMode;
}): Set<string> {
  return new Set(selectedVideoIds.filter((videoId) =>
    (perVideoState[videoId]?.localPublications ?? []).some((post) =>
      mode === "schedule"
        ? COMMITTED_PUBLICATION_STATUSES.has(post.status)
        : isImmutablePub(post),
    ),
  ));
}

/**
 * Status caption for Select-shorts tiles: commitment in words, not faces.
 * Full publish → "Posted"; partial → "N of M posted"; scheduled /
 * in-flight only → "Already scheduled" / "N already scheduled". Drafts and
 * failures show nothing. Counts posts, including repeats to one account.
 */
export function getShortSelectionSummary(
  publications: readonly Pick<PublicationModel, "accountId" | "status">[],
): ShortSelectionSummary {
  let publishedCount = 0;
  let scheduledCount = 0;
  for (const post of publications) {
    if (!COMMITTED_PUBLICATION_STATUSES.has(post.status)) continue;
    if (post.status === "published") publishedCount += 1;
    else scheduledCount += 1;
  }

  if (publishedCount > 0) {
    const countLabel = getPublishedCountLabel(publications);
    const allPublished = publishedCount === publications.length;
    return {
      label: allPublished ? "Posted" : countLabel,
      tooltip: countLabel,
    };
  }

  if (scheduledCount > 0) {
    const label =
      scheduledCount === 1
        ? "Already scheduled"
        : `${scheduledCount} already scheduled`;
    return { label, tooltip: label };
  }

  return { label: null, tooltip: null };
}
